import React, { createContext, useState, useContext, ReactNode } from 'react';

type Language = 'en' | 'zh' | 'fr' | 'ja';

interface LanguageContextType {
  language: Language;
  setLanguage: (language: Language) => void;
  translations: Record<string, string>;
}

// English translations as default
const translations = {
  en: {
    // Navbar
    home: 'Home',
    myFinances: 'My Finances',
    chatWithAdvisor: 'Chat with Advisor',
    aiIntelligence: 'AI Intelligence',
    spendingInsights: 'Spending Insights',
    riskProfile: 'Risk Profile',
    fraudProtection: 'Fraud Protection',
    accountSecurity: 'Account Security',
    personalInformation: 'Personal Information',
    savedArticles: 'Saved Articles',
    settings: 'Settings',
    logout: 'Logout',
    darkMode: 'Dark Mode',
    welcome: 'Welcome back',
    general: 'General',
    aiFeatures: 'AI Features',
    personal: 'Personal',
    
    // Settings page
    language: 'Language',
    english: 'English',
    chinese: 'Chinese',
    french: 'French',
    japanese: 'Japanese',
    
    // Common
    save: 'Save',
    cancel: 'Cancel',
    update: 'Update',
  },
  zh: {
    // Navbar
    home: '首页',
    myFinances: '我的财务',
    chatWithAdvisor: '与顾问聊天',
    aiIntelligence: 'AI 智能',
    spendingInsights: '支出洞察',
    riskProfile: '风险概况',
    fraudProtection: '欺诈保护',
    accountSecurity: '账户安全',
    personalInformation: '个人信息',
    savedArticles: '保存的文章',
    settings: '设置',
    logout: '登出',
    darkMode: '暗黑模式',
    welcome: '欢迎回来',
    general: '一般',
    aiFeatures: 'AI 功能',
    personal: '个人',
    
    // Settings page
    language: '语言',
    english: '英语',
    chinese: '中文',
    french: '法语',
    japanese: '日语',
    
    // Common
    save: '保存',
    cancel: '取消',
    update: '更新',
  },
  fr: {
    // Navbar
    home: 'Accueil',
    myFinances: 'Mes Finances',
    chatWithAdvisor: 'Discuter avec Conseiller',
    aiIntelligence: 'Intelligence AI',
    spendingInsights: 'Analyse des Dépenses',
    riskProfile: 'Profil de Risque',
    fraudProtection: 'Protection contre la Fraude',
    accountSecurity: 'Sécurité du Compte',
    personalInformation: 'Informations Personnelles',
    savedArticles: 'Articles Enregistrés',
    settings: 'Paramètres',
    logout: 'Déconnexion',
    darkMode: 'Mode Sombre',
    welcome: 'Bienvenue',
    general: 'Général',
    aiFeatures: 'Fonctionnalités IA',
    personal: 'Personnel',
    
    // Settings page
    language: 'Langue',
    english: 'Anglais',
    chinese: 'Chinois',
    french: 'Français',
    japanese: 'Japonais',
    
    // Common
    save: 'Enregistrer',
    cancel: 'Annuler',
    update: 'Mettre à jour',
  },
  ja: {
    // Navbar
    home: 'ホーム',
    myFinances: '財務情報',
    chatWithAdvisor: 'アドバイザーとチャット',
    aiIntelligence: 'AI分析',
    spendingInsights: '支出分析',
    riskProfile: 'リスクプロファイル',
    fraudProtection: '不正防止',
    accountSecurity: 'アカウントセキュリティ',
    personalInformation: '個人情報',
    savedArticles: '保存記事',
    settings: '設定',
    logout: 'ログアウト',
    darkMode: 'ダークモード',
    welcome: 'お帰りなさい',
    general: '一般',
    aiFeatures: 'AI機能',
    personal: '個人',
    
    // Settings page
    language: '言語',
    english: '英語',
    chinese: '中国語',
    french: 'フランス語',
    japanese: '日本語',
    
    // Common
    save: '保存',
    cancel: 'キャンセル',
    update: '更新',
  }
};

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

interface LanguageProviderProps {
  children: ReactNode;
}

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
  const [language, setLanguage] = useState<Language>('en');
  
  // Get translations for current language
  const currentTranslations = translations[language] || translations.en;
  
  return (
    <LanguageContext.Provider 
      value={{ 
        language, 
        setLanguage,
        translations: currentTranslations
      }}
    >
      {children}
    </LanguageContext.Provider>
  );
}; 